/**
 * The generic quoting-engine client: `/api/v1/quoting/capabilities` and
 * `/api/v1/quoting/engine/*`. Whatever engine the tenant quotes through (a
 * build-to-print demo, the next customer's), this is the contract MATIC's
 * generic workspace renders. The AES Quote Desk keeps its own client
 * (`quoting-api.ts`) and its own routes.
 *
 * Kept small on purpose: the capabilities read is imported by shared chrome
 * (sidebar, Relay's thread menu, the route gate), so this file must not pull
 * the AES client in with it.
 */
import {AuthenticatedApiClient} from '@/matic/lib/api/authenticated-api-client';
import {env} from '@/matic/config/env';

/* eslint-disable @typescript-eslint/naming-convention -- wire shapes mirror the backend's JSON (snake_case) */

/** Which workspace renders a tenant's quotes: AES's bespoke desk, or the generic one. */
export type QuotingWorkspace = 'aes-desk' | 'generic';

export type QuoteDeliverable = {
  id: string;
  label: string;
  kind: 'xlsx' | 'pdf' | 'csv' | 'eml' | string;
};

export type QuotingCapabilities =
  | {enabled: false}
  | {
      enabled: true;
      engine: string;
      workspace: QuotingWorkspace;
      supports: string[];
      deliverables: QuoteDeliverable[];
      reads: string[];
    };

/** Where a value came from. `scripted` is a demo's configured value and is shown as such. */
export type EngineBasis =
  | 'stated'
  | 'chosen'
  | 'decided'
  | 'customer'
  | 'precedent'
  | 'calculated'
  | 'default'
  | 'scripted'
  // A read value a person changed; the row's `correction` keeps what was read.
  | 'corrected'
  | 'none';

export type EngineTriage =
  | 'done'
  | 'simple'
  | 'engineering'
  | 'partial'
  | 'not_a_quote'
  // The engine is still reading the request in: its first steps are pending.
  | 'reading';

export type EngineQueueRow = {
  requestId: string;
  title: string;
  receivedAt: string | null;
  status: string;
  facts: Array<[label: string, value: string]>;
  nExports: number;
};

export type EngineRequest = {
  requestId: string;
  subject: string;
  customer: string | null;
  receivedAt: string | null;
  relayThreadId: string | null;
  /** The email as the engine read it (numbered `thread.txt` lines its `L4-L9` cites). */
  sourceText: string | null;
};

export type EngineCitation = {
  label: string;
  source: string;
  locator: string | null;
};

export type EngineAnswer = {
  label: string;
  /** Opaque to the frontend: sent back unchanged when the answer is picked. */
  token: Record<string, unknown>;
};

export type EngineQuestion = {
  id: string;
  question: string;
  answers: EngineAnswer[];
  evidence: string[];
};

export type EngineConfidence = 'high' | 'medium' | 'low';

export type EngineRow = {
  cells: Array<string | null>;
  basis: EngineBasis;
  citations: EngineCitation[];
  /** How sure a read value is; absent on rows that weren't read. */
  confidence: EngineConfidence | null;
  /** The read field the row shows, when the engine takes corrections to it. */
  key: string | null;
  correction: EngineCorrection | null;
  /** The part's read fields (Spec review keys) a derived row, like a Build operation, works from. */
  uses: string[];
  /** A stable id, on rows a person can edit. */
  id: string | null;
  /** What the row's numbers come from, in a line. */
  detail: string | null;
  /** Why the engine suggests this row, while it's neither accepted nor dismissed. */
  suggestion: string | null;
  /** The inputs changed since the row's part was reviewed, e.g. `Thickness .060 → .075`. */
  stale: string[];
  /** The parts an order-level row covers. */
  parts: string[];
};

/** A person's change to a read value: what was read (`null` if nothing), who and when. */
export type EngineCorrection = {read: string | null; by: string; at: string};

/** Who marked a part's read reviewed against its print, and when. */
export type EnginePartReview = {by: string; at: string};

/** The stepper stage a step belongs to; `generic` for an older profile's flat list. */
export type EngineStepKind =
  | 'request'
  | 'spec'
  | 'build'
  | 'costing'
  | 'price'
  | 'generic';

export type EngineStep = {
  id: string;
  title: string;
  value: string | null;
  basis: EngineBasis;
  citations: EngineCitation[];
  questions: EngineQuestion[];
  escalations: string[];
  columns: string[];
  rows: EngineRow[];
  kind: EngineStepKind;
  /** The part a per-part step is about (one of the flow's `parts`). */
  partId: string | null;
  /** `pending` while the engine is still working the step out. */
  state: 'ready' | 'pending';
  /** The columns a person can change on rows with an `id`. */
  editable: string[];
  /** What a person can add to the step: a rate card's operations. */
  options: string[];
};

/** A person's change to a derived row; see `QuoteEngineApiClient.edit`. */
export type EngineRowChange =
  | {column: string; value: string}
  | {remove: true}
  | {add: string}
  | {accept: true}
  | {dismiss: true};

export type EnginePart = {
  id: string;
  /** Part number and revision as written, e.g. `CP-210 rev B · Enclosure body`. */
  label: string;
  qty: number | null;
  sourceFile: string | null;
  /** Who checked the part through each per-part stage (`spec`, `build`), and when. */
  reviews: Partial<Record<string, EnginePartReview>>;
};

export type EngineFlow = {
  requestId: string;
  triage: EngineTriage | string;
  exportable: boolean;
  steps: EngineStep[];
  parts: EnginePart[];
};

export type EngineIngestResult = {
  requestIds: string[];
  /** Attachments the engine doesn't read: they ride along as references. */
  references: string[];
};

type WireQueueRow = {
  request_id: string;
  title: string;
  received_at: string | null;
  status: string;
  facts: Array<[string, string]>;
  n_exports: number;
};

type WireRequest = {
  request_id: string;
  subject: string;
  customer: string | null;
  received_at: string | null;
  relay_thread_id: string | null;
  source_text?: string | null;
};

type WireStep = Omit<EngineStep, 'partId' | 'rows' | 'editable' | 'options'> & {
  part_id?: string | null;
  editable?: string[];
  options?: string[];
  rows: Array<
    Pick<EngineRow, 'cells' | 'basis' | 'citations'> & {
      confidence?: string | null;
      key?: string | null;
      correction?: EngineCorrection | null;
      uses?: string[];
      id?: string | null;
      detail?: string | null;
      suggestion?: string | null;
      stale?: string[];
      parts?: string[];
    }
  >;
};

type WireFlow = {
  request_id: string;
  triage: string;
  exportable: boolean;
  steps: WireStep[];
  parts?: Array<{
    id: string;
    label: string;
    qty: number | null;
    source_file: string | null;
    reviews?: Record<string, EnginePartReview>;
  }>;
};

const toStep = ({part_id: partId, ...raw}: WireStep): EngineStep => ({
  ...raw,
  // Older engines send neither: a flat list of generic, ready steps.
  kind: raw.kind ?? 'generic',
  state: raw.state ?? 'ready',
  partId: partId ?? null,
  editable: raw.editable ?? [],
  options: raw.options ?? [],
  rows: raw.rows.map((row) => ({
    ...row,
    confidence: (row.confidence ?? null) as EngineConfidence | null,
    key: row.key ?? null,
    correction: row.correction ?? null,
    uses: row.uses ?? [],
    id: row.id ?? null,
    detail: row.detail ?? null,
    suggestion: row.suggestion ?? null,
    stale: row.stale ?? [],
    parts: row.parts ?? [],
  })),
});

const toFlow = (raw: WireFlow): EngineFlow => ({
  requestId: raw.request_id,
  triage: raw.triage,
  exportable: raw.exportable,
  // Step, question, row and citation fields are single words on the wire, but for `part_id`.
  steps: raw.steps.map((step) => toStep(step)),
  parts: (raw.parts ?? []).map((part) => ({
    id: part.id,
    label: part.label,
    qty: part.qty,
    sourceFile: part.source_file,
    reviews: part.reviews ?? {},
  })),
});

const requestPath = (id: string) =>
  `/api/v1/quoting/engine/requests/${encodeURIComponent(id)}`;

export class QuoteEngineApiClient extends AuthenticatedApiClient {
  constructor(token?: string) {
    super(token, env.AGENTS_API_URL);
  }

  /** Whether this tenant quotes, and through which workspace: the runtime gate. */
  async capabilities(): Promise<QuotingCapabilities> {
    return this.request('/api/v1/quoting/capabilities');
  }

  async queue(): Promise<EngineQueueRow[]> {
    const rows = await this.request<WireQueueRow[]>(
      '/api/v1/quoting/engine/requests'
    );
    return rows.map((r) => ({
      requestId: r.request_id,
      title: r.title,
      receivedAt: r.received_at,
      status: r.status,
      facts: r.facts,
      nExports: r.n_exports,
    }));
  }

  async getRequest(id: string): Promise<EngineRequest> {
    const r = await this.request<WireRequest>(requestPath(id));
    return {
      requestId: r.request_id,
      subject: r.subject,
      customer: r.customer,
      receivedAt: r.received_at,
      relayThreadId: r.relay_thread_id,
      sourceText: r.source_text ?? null,
    };
  }

  async flow(id: string, asOf: string): Promise<EngineFlow> {
    const raw = await this.request<WireFlow>(
      `${requestPath(id)}/flow?as_of=${encodeURIComponent(asOf)}`
    );
    return toFlow(raw);
  }

  /** Pick an answer; the engine replies with the updated flow. 422 when it refuses. */
  async answer(
    id: string,
    questionId: string,
    token: Record<string, unknown>,
    asOf: string
  ): Promise<EngineFlow> {
    const raw = await this.request<WireFlow>(`${requestPath(id)}/answer`, {
      method: 'POST',
      body: JSON.stringify({question_id: questionId, token, as_of: asOf}),
    });
    return toFlow(raw);
  }

  /** Correct a read field (`partId` null for the request's own); 422 when refused. */
  async correct(
    id: string,
    field: {partId: string | null; key: string; value: string},
    asOf: string
  ): Promise<EngineFlow> {
    const raw = await this.request<WireFlow>(`${requestPath(id)}/corrections`, {
      method: 'POST',
      body: JSON.stringify({
        part_id: field.partId,
        key: field.key,
        value: field.value,
        as_of: asOf,
      }),
    });
    return toFlow(raw);
  }

  /** Mark a part reviewed at one per-part stage (`spec`, `build`), or clear the mark. */
  async review(
    id: string,
    mark: {partId: string; stage: string; reviewed: boolean},
    asOf: string
  ): Promise<EngineFlow> {
    const raw = await this.request<WireFlow>(`${requestPath(id)}/reviews`, {
      method: 'POST',
      body: JSON.stringify({
        part_id: mark.partId,
        stage: mark.stage,
        reviewed: mark.reviewed,
        as_of: asOf,
      }),
    });
    return toFlow(raw);
  }

  /** Change a derived row (minutes, a sheet price), or add, remove, accept or dismiss one. */
  async edit(
    id: string,
    target: {stepId: string; rowId: string | null; change: EngineRowChange},
    asOf: string
  ): Promise<EngineFlow> {
    const raw = await this.request<WireFlow>(`${requestPath(id)}/edits`, {
      method: 'POST',
      body: JSON.stringify({
        step_id: target.stepId,
        row_id: target.rowId,
        change: target.change,
        as_of: asOf,
      }),
    });
    return toFlow(raw);
  }

  async exports(id: string): Promise<string[]> {
    const r = await this.request<{files: string[]}>(
      `${requestPath(id)}/exports`
    );
    return r.files;
  }

  /** Produce a deliverable; the bytes come back as an attachment. */
  async exportDeliverable(
    id: string,
    deliverableId: string,
    asOf: string
  ): Promise<Blob> {
    return this.postBlob(`${requestPath(id)}/export`, {
      deliverable_id: deliverableId,
      as_of: asOf,
    });
  }

  /** Quote a Relay thread through the tenant's engine. */
  async ingestThread(threadId: string): Promise<EngineIngestResult> {
    const r = await this.request<{request_ids: string[]; references: string[]}>(
      `/api/v1/quoting/engine/threads/${encodeURIComponent(threadId)}/ingest`,
      {method: 'POST', timeoutMs: 150_000}
    );
    return {requestIds: r.request_ids, references: r.references};
  }
}

const clientCache = new Map<string, QuoteEngineApiClient>();
export function getQuoteEngineApi(token?: string): QuoteEngineApiClient {
  const key = token ?? 'no-token';
  if (!clientCache.has(key))
    clientCache.set(key, new QuoteEngineApiClient(token));
  return clientCache.get(key)!;
}

/* eslint-enable @typescript-eslint/naming-convention */
